import React, { useEffect } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import ChatInterface from './components/ChatInterface';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AIDispatcherDashboard from './pages/AIDispatcherDashboard';
import DispatchRunsPage from './pages/DispatchRunsPage';
import DispatchRunDetailPage from './pages/DispatchRunDetailPage';
import theme from './theme';
import muiTheme from './theme-mui';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { APP_NAME } from './constants/branding';
import { MsalRedirectHandler } from './components/MsalRedirectHandler';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Component to wrap Material UI components with MUI theme provider
const MaterialUIWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <MuiThemeProvider theme={muiTheme}>
      {children}
    </MuiThemeProvider>
  );
};

function App() {
  // Set default document title
  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <Router>
        <AuthProvider>
          <MsalRedirectHandler>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <ChatInterface />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai-dispatcher-dashboard"
                element={
                  <PrivateRoute>
                    <MaterialUIWrapper>
                      <AIDispatcherDashboard />
                    </MaterialUIWrapper>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dispatch-control-center"
                element={
                  <PrivateRoute>
                    <MaterialUIWrapper>
                      <DispatchRunsPage />
                    </MaterialUIWrapper>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dispatch-control-center/:runId"
                element={
                  <PrivateRoute>
                    <MaterialUIWrapper>
                      <DispatchRunDetailPage />
                    </MaterialUIWrapper>
                  </PrivateRoute>
                }
              />
            </Routes>
          </MsalRedirectHandler>
        </AuthProvider>
      </Router>
    </ChakraProvider>
  );
}

export default App; 